import { DocumentTemplateShell, TemplateEmptyState } from './TemplateShared';
import { DASH, formatTimestamp, type TemplatePatient } from './templateUtils';

export interface VaccinationsTemplateRow {
  id?: string;
  vaccine?: string;
  route?: string;
  status?: string;
  dose?: string;
  occurrenceTime?: string;
  site?: string;
}

export interface VaccinationsTemplateProps {
  rows: VaccinationsTemplateRow[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightRowId?: string;
}

const headers = [
  'Vaccine',
  'Route',
  'Status',
  'Dose',
  'Occurrence Time',
  'Site',
] as const;

export const VaccinationsTemplate = ({
  rows,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightRowId,
}: VaccinationsTemplateProps) => {
  const items = rows ?? [];

  return (
    <DocumentTemplateShell
      title="Immunizations Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
    >
      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        {items.length === 0 ? (
          <TemplateEmptyState message="No immunizations available" />
        ) : (
          <div className="overflow-hidden rounded-sm border border-zinc-200">
            <div className="flex bg-slate-100">
              {headers.map((h, idx) => (
                <div
                  key={h}
                  className={`flex flex-1 px-3 py-2.5 ${
                    idx < headers.length - 1 ? 'border-r border-zinc-300' : ''
                  }`}
                >
                  <p className="flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
                    {h}
                  </p>
                </div>
              ))}
            </div>
            {items.map((row, idx) => {
              const isHighlighted =
                !!highlightRowId && row.id === highlightRowId;
              const zebra = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
              const rowBg = isHighlighted ? 'bg-amber-100' : zebra;
              const cells = [
                row.vaccine,
                row.route,
                row.status,
                row.dose,
                formatTimestamp(row.occurrenceTime, DASH),
                row.site,
              ];
              return (
                <div
                  key={row.id ?? `vx-${idx}`}
                  id={row.id ? `vaccination-${row.id}` : undefined}
                  tabIndex={isHighlighted ? -1 : undefined}
                  className={`flex border-t border-zinc-200 ${rowBg}`}
                >
                  {cells.map((cell, colIdx) => (
                    <div
                      key={headers[colIdx]}
                      className={`flex flex-1 px-3 py-2.5 ${
                        colIdx < cells.length - 1
                          ? 'border-r border-zinc-200'
                          : ''
                      }`}
                    >
                      <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
                        {cell?.toString().trim() || DASH}
                      </p>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </DocumentTemplateShell>
  );
};
